import { io, type Socket } from 'socket.io-client'
import { useAuthStore } from '../store/authStore.js'
import { useClientAuthStore } from '../store/clientAuthStore.js'
import { loginPathFor } from '../auth/loginPath.js'
import { doRefresh } from '../api/client.js'
import type { ClientToServerEvents, ServerToClientEvents } from './types.js'

export type GatewaySocket = Socket<ServerToClientEvents, ClientToServerEvents>

let socket: GatewaySocket | null = null
/** One refresh-and-retry per outage; cleared by the next successful connect. */
let authRetryUsed = false

export interface SocketClientOptions {
  url?: string
  path?: string
}

/** Session ended because the account signed in elsewhere (single-session guard). */
function handleSessionEnded(): void {
  try { sessionStorage.setItem('kc3.logoutReason', 'replaced') } catch { /* private mode */ }
  handleSocketAuthError()
}

function handleSocketAuthError(): void {
  const { clearSession, user } = useAuthStore.getState()
  if (user) {
    console.warn('[Socket] Authentication error - forcing logout')
    clearSession()
    disconnectSocket()
    window.location.href = loginPathFor(window.location.pathname)
  }
}

/** The socket if one was already opened — never connects a new one. */
export function getExistingSocket(): GatewaySocket | null {
  return socket ?? null
}

export function getSocket(opts: SocketClientOptions = {}): GatewaySocket {
  if (socket) return socket
  const url =
    opts.url ??
    (import.meta.env['VITE_WS_URL'] as string | undefined) ??
    window.location.origin

  console.log('[Socket] Connecting to:', url, 'path:', opts.path ?? '/ws/socket.io')

  socket = io(url, {
    path: opts.path ?? '/ws/socket.io',
    auth: (cb: (data: { token?: string }) => void) => {
      const agentToken = (useAuthStore.getState() as any).accessToken
      const clientToken = useClientAuthStore.getState().accessToken
      cb({ token: clientToken || agentToken })
    },
    // ITEM 69 / §4.4 — skip the HTTP long-polling handshake.
    //
    // Socket.IO's default is to poll first and then upgrade, which is only
    // safe when every request in the handshake reaches the SAME server.
    // ws-gateway runs multiple replicas and nginx proxies with a bare
    // `proxy_pass http://ws-gateway:3001/` (docker/web/nginx.conf:28) — no
    // upstream block, no ip_hash, no sticky cookie — so Docker's DNS VIP
    // round-robins every request. The handshake starts on replica A, the next
    // poll lands on replica B which has never seen that session id, and the
    // client drops into a reconnect loop. It presents as flaky sockets, not as
    // a load-balancer problem.
    //
    // Websocket-only means one connection to one server, which REMOVES the
    // stickiness requirement rather than depending on infrastructure to meet
    // it. The trade is that a network which blocks WebSocket has no fallback —
    // acceptable for an internal agent console, and preferable to a
    // silently-degrading polling path that only breaks under multi-replica
    // load.
    transports: ['websocket'],
    withCredentials: true,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  }) as GatewaySocket

  // Connection events for debugging
  socket.on('connect', () => {
    authRetryUsed = false
    console.log('[Socket] Connected successfully - socket id:', socket?.id)
  })

  socket.on('disconnect', (reason: string) => {
    console.log('[Socket] Disconnected - reason:', reason)
  })

  // Handle connection errors
  socket.on('connect_error', (err: Error) => {
    console.error('[Socket] CONNECT_ERROR:', err.message)
    console.error('[Socket] Error details:', err)
    if (err.message === 'SESSION_REPLACED') {
      handleSessionEnded()
    } else if (err.message?.includes('auth') || err.message?.includes('unauthorized') || err.message?.includes('Authentication')) {
      // SE-08: access tokens are short-lived, so a reconnect after an idle spell can present an expired cookie.
      // Refresh once and retry before treating it as a lost session.
      if (!authRetryUsed) {
        authRetryUsed = true
        void doRefresh().then((ok) => {
          if (ok) socket?.connect()
          else handleSocketAuthError()
        })
      } else {
        handleSocketAuthError()
      }
    }
  })

  // The gateway kicks this browser when a newer login replaced its session.
  ;(socket as unknown as { on: (event: string, cb: (d: { reason?: string }) => void) => void }).on('auth:session_ended', (d) => {
    if (d?.reason === 'replaced') handleSessionEnded()
    else handleSocketAuthError()
  })

  // Track reconnection attempts (use type assertion for reserved events)
  ;(socket as unknown as { on: (event: string, cb: (attempt: number) => void) => void }).on('reconnect_attempt', (attempt: number) => {
    console.log('[Socket] Reconnection attempt:', attempt)
  })

  ;(socket as unknown as { on: (event: string, cb: () => void) => void }).on('reconnect_failed', () => {
    console.error('[Socket] Reconnection failed after all attempts')
  })

  return socket
}

export function disconnectSocket(): void {
  socket?.disconnect()
  socket = null
}

export function __resetSocketForTests(next: GatewaySocket | null): void {
  socket = next
  authRetryUsed = false
}
